import { API_BASE } from './api-base';
import { browserTimeZone } from './browser-time-zone';

/**
 * Hand the call off to text — get something out of a call the user can still act on after hanging
 * up.
 *
 * The spoken turns already land in the conversation, but a transcript is not an artifact: it has no
 * links, no entities, nothing to click. So the handoff runs ONE turn through the same assistant
 * loop the chat uses, in the same conversation, and lets the assistant write the message. That is
 * what makes the result real — an actual assistant message, persisted by the loop, with the real
 * links and entities in it — rather than a copy of what was said.
 *
 * Deliberately NOT handed back to the voice model: it is not an answer to speak, and the model
 * narrating a chat message it didn't write is exactly the forward-narration the prompt forbids. The
 * caller shows a small local confirmation instead.
 */
export const VOICE_HANDOFF_REQUEST =
  'Post a message in this chat summing up what we have covered on this call so far and anything ' +
  'I should act on, with the real links or entities where they exist. Keep it short and skimmable.';

/**
 * Runs the handoff and resolves once the assistant turn has completed (its message is persisted by
 * then). The streamed reply is drained rather than parsed — the artifact is in the conversation,
 * not in this response.
 */
export async function sendVoiceHandoff(conversationId: string): Promise<boolean> {
  if (!conversationId) return false;
  try {
    const res = await fetch(`${API_BASE}/api/voice/ask`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ conversationId, request: VOICE_HANDOFF_REQUEST, deviceTimeZone: browserTimeZone() }),
    });
    if (!res.ok) return false;
    // Drain so the request isn't aborted mid-turn by the connection being dropped.
    await res.text().catch(() => '');
    return true;
  } catch {
    return false;
  }
}
